// Chart helpers shared by the Curves gallery, the chart page and the Requirements curve panel: topic labels in a fixed order, the lines of a chart as CurveChart series (one line as the required profile, several in categorical colours), its axes, the kind-specific small print, the chart key of a curve and the document search param.
import type { ChartDetail, ChartKind, ChartLine, ChartTopic, CurveOut } from "@/api/__generated__";

import type { CurveAxis, CurveSeries } from "./CurveChart";

export const TOPIC_ORDER: readonly ChartTopic[] = [
  "frt",
  "frequency",
  "voltage",
  "reactive_power",
  "active_power",
  "power_quality",
  "other",
];

export const TOPIC_LABELS: Record<ChartTopic, string> = {
  frt: "Fault ride-through",
  frequency: "Frequency",
  voltage: "Voltage",
  reactive_power: "Reactive power",
  active_power: "Active power",
  power_quality: "Power quality",
  other: "Other",
};

export const SMALL_PRINT: Record<ChartKind, string> = {
  figure: "Values were read from the figure automatically. The original figure is authoritative.",
  table: "Values were read from the table automatically. The original table is authoritative.",
  text: "Values were read from the clause text automatically. The clause text is authoritative.",
};

const FIGURE_MARK = "#fig:";
const TABLE_MARK = "#tab:";

export function lineSeries(lines: readonly ChartLine[]): CurveSeries[] {
  const single = lines.length === 1;
  return lines.map((line, index) => ({
    id: line.line_key,
    label: line.label,
    role: single ? "required" : "line",
    points: line.points.map((point) => ({ x: point.x, y: point.y })),
    interpolation: line.interpolation,
    region: line.region,
    seriesIndex: index,
    rule: line.rule_text,
  }));
}

export function chartAxes(chart: Pick<ChartDetail, "axes">): { xAxis: CurveAxis; yAxis: CurveAxis } {
  const { x, y } = chart.axes;
  return {
    xAxis: { quantity: x.quantity, unit: x.unit, base: x.base },
    yAxis: { quantity: y.quantity, unit: y.unit, base: y.base },
  };
}

export function chartKeyOf(curve: Pick<CurveOut, "chart_key" | "figure_uid" | "table_uid">): string | null {
  if (curve.chart_key) {
    return curve.chart_key;
  }
  if (curve.figure_uid?.includes(FIGURE_MARK)) {
    return `fig-${curve.figure_uid.slice(curve.figure_uid.indexOf(FIGURE_MARK) + FIGURE_MARK.length)}`;
  }
  if (curve.table_uid?.includes(TABLE_MARK)) {
    return `tab-${curve.table_uid.slice(curve.table_uid.indexOf(TABLE_MARK) + TABLE_MARK.length)}`;
  }
  return null;
}

export function docSearch(docUid: string, currentDocUid: string | null): { doc?: string } {
  return docUid === currentDocUid ? {} : { doc: docUid };
}

export function sortTopics(topics: Iterable<ChartTopic>): ChartTopic[] {
  const present = new Set(topics);
  return TOPIC_ORDER.filter((topic) => present.has(topic));
}
